import { useState, useEffect } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme, Skeleton, FormHelperText, Box } from '@mui/material';
import { GetServerSideProps, NextPage } from 'next';
import { useTranslation, Trans } from 'next-i18next';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';

import FormPageContainer from '~/components/shared/FormPageContainer';
import Layout from '~/components/shared/Layout';
import Link from '~/components/shared/Link';
import LoadingButton from '~/components/shared/LoadingButton';
import PasswordMinCharactersDisplay from '~/components/shared/PasswordMinCharactersDisplay';
import { FormHeader } from '~/components/shared/forms/FormHeader';
import { Events } from '~/constants/events';
import { trackEvent } from '~/helpers/analytics';
import AuthHelpers from '~/helpers/auth';
import Errors from '~/helpers/errors';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';
import { getTranslations } from '~/lib/next-i18next';
import { useIsResetTokenValidQuery, useResetPasswordMutation } from '~/networking/schema';

const { TextField, Typography, Checkbox } = ToucanComponents;

interface ResetPasswordInputs {
  password: string;
  passwordConfirmation: string;
}

const ResetPassword: NextPage = () => {
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordInputs>();
  const [resetPassword] = useResetPasswordMutation();
  const [wasResetSuccessful, setWasResetSuccessful] = useState(false);
  const [isResetProcessing, setIsResetProcessing] = useState(false);
  const [didResetFail, setDidResetFail] = useState(false);
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(true);
  const { refreshUser } = useLoggedInUser();
  const { t } = useTranslation('reset-password');
  const { query } = useRouter();
  const token = Array.isArray(query?.token) ? query.token[0] : query?.token;
  const { data, loading } = useIsResetTokenValidQuery({
    variables: {
      token: token || '',
    },
  });

  useEffect(() => {
    trackEvent({
      name: Events.ResetPasswordScreenViewed,
    });
  }, []);

  const onResetPassword = async (inputs: ResetPasswordInputs) => {
    const variables = { token: token || '', newPassword: inputs.password };
    const response = await resetPassword({ variables });

    setIsResetProcessing(true);

    try {
      if (response.data?.resetPassword?.jwt && response.data.resetPassword.refreshToken) {
        AuthHelpers.setAuthCookies({
          token: response.data.resetPassword.jwt,
          refreshToken: response.data.resetPassword.refreshToken,
        });

        await refreshUser();

        trackEvent({
          name: Events.SuccessfullyResetPassword,
        });

        setWasResetSuccessful(true);
      } else {
        setDidResetFail(true);
        setIsResetProcessing(false);
      }
    } catch {
      setDidResetFail(true);
      setIsResetProcessing(false);
    }
  };

  return (
    <Layout pageError={Errors.ResetPasswordPageError}>
      <FormPageContainer>
        {loading ? (
          <Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height="45px"
              sx={{
                borderRadius: '4px',
              }}
            />
            <Skeleton
              variant="rectangular"
              width="190px"
              height="25px"
              sx={{
                borderRadius: '4px',
                mt: 1,
              }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height="74px"
              sx={{
                borderRadius: '4px',
                mt: 4,
              }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height="74px"
              sx={{
                borderRadius: '4px',
                mt: 4,
              }}
            />
            <Skeleton
              variant="rectangular"
              width="156px"
              height="25px"
              sx={{
                mt: 2,
              }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height="48px"
              sx={{
                borderRadius: '4px',
                mt: 2,
              }}
            />
          </Box>
        ) : (
          <>
            {!data?.isResetTokenValid ? (
              <>
                <Typography
                  variant="lg"
                  font="secondary"
                  css={css`
                    margin-bottom: ${theme.spacing(2)};
                  `}
                >
                  {t('resetPasswordUhOh')}
                </Typography>
                <Typography variant="sm" color={ToucanColors.gray[400]}>
                  <Trans
                    i18nKey="resetPasswordToReset"
                    ns="reset-password"
                    values={{ forgotPassword: t('resetPasswordForgotPassword') }}
                    components={[<Link href="/forgot-password">{t('resetPasswordForgotPassword')}</Link>]}
                  />
                </Typography>
              </>
            ) : (
              <>
                <FormHeader subHeader={wasResetSuccessful ? '' : t('resetPasswordNewPassword')}>
                  {t('resetPasswordGetYouBackIn')}
                </FormHeader>
                {wasResetSuccessful ? (
                  <Typography variant="sm" color={ToucanColors.gray[400]}>
                    <Trans
                      i18nKey="resetPasswordChangedPassword"
                      ns="reset-password"
                      values={{ dashboard: t('resetPasswordDashboard') }}
                      components={[<Link href="/dashboard">{t('resetPasswordDashboard')}</Link>]}
                    />
                  </Typography>
                ) : (
                  <>
                    <form
                      css={css`
                        margin-bottom: ${theme.spacing(2)};
                      `}
                    >
                      <TextField
                        type={isPasswordRevealed ? 'text' : 'password'}
                        label={t('resetPasswordPassword')}
                        hasError={!!errors.password}
                        helperText={
                          errors.password ? (
                            errors.password.message
                          ) : (
                            <PasswordMinCharactersDisplay passwordLength={watch('password')?.length ?? 0} />
                          )
                        }
                        css={css`
                          margin-bottom: ${theme.spacing(1)};
                        `}
                        {...register('password', {
                          required: `${t('resetPasswordLengthRequired')}`,
                          minLength: {
                            value: 8,
                            message: t('resetPasswordLengthRequired'),
                          },
                        })}
                        hideCta={t('hide')}
                        showCta={t('show')}
                      />
                      <TextField
                        type={isPasswordRevealed ? 'text' : 'password'}
                        label={t('resetPasswordConfirmPassword')}
                        hasError={!!errors.passwordConfirmation}
                        helperText={errors.passwordConfirmation?.message}
                        {...register('passwordConfirmation', {
                          required: `${t('resetPasswordPleaseConfirmPassword')}`,
                          validate: (value) => value === watch('password') || t('resetPasswordDoesntMatchTryAgain'),
                        })}
                        hideCta={t('hide')}
                        showCta={t('show')}
                      />
                      {didResetFail && (
                        <FormHelperText error>
                          <Trans
                            i18nKey="resetPasswordExperiencedIssues"
                            ns="reset-password"
                            values={{ contactUs: t('resetPasswordContactUs') }}
                            components={[<Link href="/contact">{t('resetPasswordContactUs')}</Link>]}
                          />
                        </FormHelperText>
                      )}
                      <div
                        css={css`
                          margin-top: ${theme.spacing(2)};
                        `}
                      >
                        <Checkbox
                          isChecked={isPasswordRevealed}
                          label={t('resetPasswordShowPassword')}
                          value={t('resetPasswordShowHidePassword')}
                          onChange={() => setIsPasswordRevealed((prevState) => !prevState)}
                        />
                      </div>
                    </form>
                    <LoadingButton
                      size="medium"
                      type="utility"
                      onClick={handleSubmit(onResetPassword)}
                      css={css`
                        width: 100%;
                      `}
                      isLoading={isResetProcessing}
                    >
                      {t('resetPasswordReset')}
                    </LoadingButton>
                  </>
                )}
              </>
            )}
          </>
        )}
      </FormPageContainer>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => ({
  props: {
    ...(await getTranslations({
      locale,
      namespaces: 'reset-password',
    })),
  },
});

export default ResetPassword;
